import { FunctionComponent, useEffect, useRef } from "react";

import { classNames } from "@/utils/classNames";
import { sanitizeMessageHtml } from "@/utils/sanitizeMessageHtml";

interface IsolatedHtmlProps {
	html: string;
	className?: string;
}

/**
 * Renders arbitrary HTML inside a Shadow DOM root so that any `<style>` tags
 * in the HTML cannot leak into (or be affected by) the rest of the application.
 *
 * The HTML is sanitized here rather than at the call sites: this is the single
 * place every message body passes through, so no future caller can forget it.
 * The shadow root is a style boundary only — script inside one runs with the
 * page's full privileges — and these bodies are written by strangers, so the
 * sanitizer is what makes the assignment below safe (PREC-3957).
 */
export const IsolatedHtml: FunctionComponent<IsolatedHtmlProps> = ({ html, className }) => {
	const hostRef = useRef<HTMLDivElement>(null);
	const shadowRef = useRef<ShadowRoot | null>(null);

	useEffect(() => {
		if (!hostRef.current) {
			return;
		}

		if (!shadowRef.current) {
			shadowRef.current = hostRef.current.attachShadow({ mode: "open" });
		}

		shadowRef.current.innerHTML = `
            <style>
                /* Layout containment, not just style containment: a shadow root scopes
                   selectors, it does not bound layout. A mail whose own CSS positions an
                   element absolutely resolves it against the nearest positioned ancestor
                   *outside* this host, escapes the host's overflow-hidden, and grows the
                   document — the whole page then scrolls (PREC-4114). Containment makes the
                   host the containing block for absolute/fixed descendants and clips them. */
                :host { display: block; max-width: 100%; contain: layout paint; }
                * { 
                    max-width: 100%; 
                    word-break: break-word; 
                    overflow-wrap: break-word;
                    box-sizing: border-box;
                }
                img { height: auto; }
                pre { white-space: pre-wrap; }
                table { width: 100% !important; }
            </style>
            ${sanitizeMessageHtml(html)}
        `;
	}, [html]);

	return (
		<div ref={hostRef} className={classNames("w-full max-w-full overflow-hidden", className)} />
	);
};
